export function initializeCarousel() {
    function clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }

    function createCarousel(root) {
        const viewport = root.querySelector('.carousel__viewport');
        const slides = Array.from(root.querySelectorAll('.carousel__slide'));
        const btnPrev = root.querySelector('.carousel__btn--prev');
        const btnNext = root.querySelector('.carousel__btn--next');
        const dotsContainer = root.querySelector('.carousel__dots');
        const status = root.querySelector('[aria-live]');

        const itemsPerSlide = viewport.dataset.itemsPerSlide ? parseInt(viewport.dataset.itemsPerSlide, 10) : 4;
        const ITEM_WIDTH = 220;
        const ITEM_GAP = 30;
        const AUTO_DELAY = 8000;

        let itemsPerView = 1;
        let pageCount = 1;
        let currentPage = 0;
        let isDragging = false;
        let startX = 0;
        let lastX = 0;
        let animationFrame = 0;
        let autoTimer = 0;
        let direction = 'right';

        function createDots() {
            dotsContainer.innerHTML = '';
            for (let i = 0; i < pageCount; i += 1) {
                const dot = document.createElement('button');
                dot.className = 'carousel__dot';
                dot.type = 'button';
                dot.setAttribute('role', 'tab');
                dot.setAttribute('aria-label', `Go to page ${i + 1}`);
                dot.addEventListener('click', () => setPage(i));
                dotsContainer.appendChild(dot);
            }
        }

        function updateDots() {
            const dots = dotsContainer.querySelectorAll('.carousel__dot');
            dots.forEach((d, i) => d.setAttribute('aria-selected', String(i === currentPage)));
        }

        function scheduleAuto() {
            clearTimeout(autoTimer);
            autoTimer = setTimeout(() => {
                onNext();
            }, AUTO_DELAY);
        }

        function renderPage() {
            const startIndex = currentPage * itemsPerView;
            const endIndex = Math.min(startIndex + itemsPerView, slides.length);

            slides.forEach((slide, i) => {
                const visible = i >= startIndex && i < endIndex;

                if (visible) {
                    slide.classList.add(`carousel__slide--enter-${direction}`);
                    slide.classList.remove('carousel__slide--hidden');
                    slide.classList.remove('carousel__slide--visible');

                    setTimeout(() => {
                        slide.classList.remove(`carousel__slide--enter-${direction}`);
                        slide.classList.add('carousel__slide--visible');
                    }, 50);
                } else {
                    slide.classList.add('carousel__slide--hidden');
                    slide.classList.remove('carousel__slide--visible');
                }

                slide.toggleAttribute('hidden', !visible);
                slide.setAttribute('aria-hidden', String(!visible));
                if ('inert' in slide) slide.inert = !visible;
            });
        }

        function setPage(newPage, announce = true) {
            const maxPage = pageCount - 1;
            currentPage = clamp(newPage, 0, maxPage);
            renderPage();
            updateDots();
            if (announce && status) {
                const start = currentPage * itemsPerView + 1;
                const end = Math.min(slides.length, start + itemsPerView - 1);
                status.textContent = `Showing items ${start}-${end} of ${slides.length}`;
            }

            scheduleAuto();
        }

        function onPrev() {
            direction = 'left';
            const prevPage = (currentPage - 1 + pageCount) % pageCount;
            setPage(prevPage);
        }
        function onNext() {
            direction = 'right';
            const nextPage = (currentPage + 1) % pageCount;
            setPage(nextPage);
        }

        function onKeydown(e) {
            if (document.activeElement !== viewport) return;
            switch (e.key) {
                case 'ArrowLeft': e.preventDefault(); onPrev(); break;
                case 'ArrowRight': e.preventDefault(); onNext(); break;
                case 'Home': e.preventDefault(); setPage(0); break;
                case 'End': e.preventDefault(); setPage(pageCount - 1); break;
            }
        }

        function animation() { if (isDragging) requestAnimationFrame(animation); }
        function pointerDown(clientX) {
            isDragging = true;
            startX = clientX;
            lastX = clientX;
            animationFrame = requestAnimationFrame(animation);
            viewport.classList.add('grabbing');
        }
        function pointerMove(clientX) {
            if (!isDragging) return;
            lastX = clientX;
        }
        function pointerUp() {
            cancelAnimationFrame(animationFrame);
            isDragging = false;
            const movedBy = lastX - startX;
            const threshold = 50;
            if (Math.abs(movedBy) > threshold) {
                if (movedBy < 0) onNext(); else onPrev();
            }
            viewport.classList.remove('grabbing');
        }

        viewport.addEventListener('mousedown', (e) => {
            e.preventDefault();
            pointerDown(e.clientX);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp, { once: true });
        });
        function onMouseMove(e) { pointerMove(e.clientX); }
        function onMouseUp() {
            window.removeEventListener('mousemove', onMouseMove);
            pointerUp();
        }

        viewport.addEventListener('touchstart', (e) => {
            if (e.touches.length !== 1) return;
            pointerDown(e.touches[0].clientX);
        }, { passive: true });
        viewport.addEventListener('touchmove', (e) => {
            if (e.touches.length !== 1) return;
            pointerMove(e.touches[0].clientX);
        }, { passive: true });
        viewport.addEventListener('touchend', pointerUp);
        viewport.addEventListener('touchcancel', pointerUp);

        btnPrev?.addEventListener('click', onPrev);
        btnNext?.addEventListener('click', onNext);
        document.addEventListener('keydown', onKeydown);

        requestAnimationFrame(() => {
            // 1. Temporarily force-hide all slides (removes them from layout)
            slides.forEach(slide => {
                slide.style.display = 'none';
            });

            // 2. Measure the pure available width (padding accounted for, no slide influence)
            const cs = getComputedStyle(viewport);
            const paddingLeft = parseFloat(cs.paddingLeft) || 0;
            const paddingRight = parseFloat(cs.paddingRight) || 0;
            const availableWidth = viewport.clientWidth - paddingLeft - paddingRight;

            // 3. Restore original display values (slides become visible again)
            slides.forEach(slide => {
                slide.style.display = '';
            });

            // 4. Compute how many items fit
            const possible = Math.max(1, Math.floor((availableWidth + ITEM_GAP) / (ITEM_WIDTH + ITEM_GAP)));
            const newItemsPerView = Math.min(itemsPerSlide, possible);
            itemsPerView = newItemsPerView;
            pageCount = Math.max(1, Math.ceil(slides.length / itemsPerView));

            // 5. Initialize dots and show the first page
            createDots();
            setPage(0, false);
        });

        return {
            goTo: setPage,
            next: onNext,
            prev: onPrev,
            destroy() {
                btnPrev?.removeEventListener('click', onPrev);
                btnNext?.removeEventListener('click', onNext);
                document.removeEventListener('keydown', onKeydown);
                clearTimeout(autoTimer);
            }
        };
    }

    (() => {
        const carousels = document.querySelectorAll('.carousel');
        if (!carousels.length) return;
        carousels.forEach(createCarousel);
    })();
}